import * as React from 'react'
import { Switch as SwitchPrimitive } from 'radix-ui'

import { cn } from 'src/lib/utils'

/* -------------------------------------------------------------------------
 * shadcn `Switch` primitive, from the new-york registry via the CLI
 * (`npx shadcn@latest add switch`), verbatim apart from the repo's quote
 * style. Backs the `src/antd/Switch` seam's shadcn half
 * (`src/antd/Switch.shadcn.tsx`); nothing else should import it, and nothing
 * reaches it until someone flips the design toggle.
 *
 * forwardRef is the one edit to the registry's output: it writes plain function
 * components (React 19's ref-as-prop) and this repo is on the React 18 runtime
 * with `@types/react` pinned to 17, so a ref handed to one is dropped with a
 * console warning. See `src/components/ui/checkbox.tsx`.
 *
 * `size` is the registry's own prop and maps onto antd's two sizes — antd's
 * `size="small"` is this `size="sm"`, antd's default is this `default`. The
 * seam does that translation so no call site changes.
 *
 * `children` is the second edit. The registry renders the Thumb as the Root's
 * ONLY child, but antd draws `checkedChildren` / `unCheckedChildren` INSIDE
 * the track and widens the track to fit them — a label that is absolutely
 * positioned contributes nothing to the track's intrinsic width, so it gets
 * clipped by the thumb. Anything passed as `children` is therefore rendered
 * inside the Root, after the Thumb, so it participates in the Root's flex
 * layout. Which SIDE it lands on is the seam's business, not this file's: the
 * label sits opposite the thumb, so `src/antd/Switch.shadcn.tsx` flips it with
 * an `order-*` utility rather than this component re-ordering its children.
 * ------------------------------------------------------------------------- */
const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentProps<typeof SwitchPrimitive.Root> & {
    size?: 'sm' | 'default'
  }
>(({ className, size = 'default', children, ...props }, ref) => {
  return (
    <SwitchPrimitive.Root
      ref={ref}
      data-slot="switch"
      data-size={size}
      className={cn(
        'peer group/switch inline-flex shrink-0 items-center rounded-full border border-transparent shadow-xs transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-[1.15rem] data-[size=default]:w-8 data-[size=sm]:h-3.5 data-[size=sm]:w-6 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80',
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className={cn(
          'pointer-events-none block rounded-full bg-background ring-0 transition-transform group-data-[size=default]/switch:size-4 group-data-[size=sm]/switch:size-3 data-[state=checked]:translate-x-[calc(100%-2px)] data-[state=unchecked]:translate-x-0 dark:data-[state=checked]:bg-primary-foreground dark:data-[state=unchecked]:bg-foreground'
        )}
      />
      {children}
    </SwitchPrimitive.Root>
  )
})
Switch.displayName = 'Switch'

export { Switch }
